import { Fragment, type ReactElement } from 'react'
import { isEmpty, isNotEmpty, type NotificationItem } from '@terros/common'
import { Alert, Button, Loader, Stack, Text, Title } from '@mantine/core'
import { type GetInfiniteHookResponse } from '@front/model'
import { NotificationRow } from './NotificationRow'
import { notificationDay } from './helpers'

type Props = { query: GetInfiniteHookResponse<NotificationItem>; onOpen?: VoidFunction }

export function NotificationList({ query, onOpen }: Props): ReactElement {
  const { data, error, isLoading, isSuccess } = query
  if (isLoading || (isEmpty(data) && !isSuccess && isEmpty(error)))
    return (
      <Stack align='center' p='xl'>
        <Loader aria-label='Loading notifications' />
      </Stack>
    )
  return (
    <Stack gap={0}>
      {isNotEmpty(error) && (
        <Alert color='red' title='Could not load notifications' role='alert'>
          {error}
          <Button variant='subtle' onClick={query.refetch}>
            Retry
          </Button>
        </Alert>
      )}
      {isEmpty(data) && isEmpty(error) && (
        <Text c='dimmed' ta='center' p='xl'>
          No notifications here.
        </Text>
      )}
      {data.map((item, index): ReactElement => {
        const day = notificationDay(item.createdAt)
        const previous = data[index - 1]
        const showHeading = isEmpty(previous) || notificationDay(previous.createdAt) !== day
        return (
          <Fragment key={item.id}>
            {showHeading && (
              <Title order={2} size={11} fw={600} c='dimmed' tt='uppercase' px={12} pt={16} pb={8}>
                {day}
              </Title>
            )}
            <NotificationRow item={item} onOpen={onOpen} />
          </Fragment>
        )
      })}
      {query.hasNextPage && (
        <Button variant='subtle' onClick={query.fetchNextPage} loading={query.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </Stack>
  )
}
